import { useCallback, useEffect, useState } from 'react';
import { extractReceivablesError, getAging } from '../services';
import { IAgingData, Segmento } from '../types';

interface AgingState {
	data: IAgingData | null;
	loading: boolean;
	error: string | null;
	cachedAt: string | null;
	refetch: (opts?: { refresh?: boolean }) => void;
}

/**
 * El aging completo. `refresh` fuerza el recálculo en el backend (el informe se
 * cachea 5 minutos), para el botón de actualizar.
 */
export function useAging(filters: { segments: Segmento[]; agencyId?: number; asOf: string }): AgingState {
	const { segments, agencyId, asOf } = filters;
	const key = segments.join(',');

	const [data, setData] = useState<IAgingData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [cachedAt, setCachedAt] = useState<string | null>(null);
	const [tick, setTick] = useState(0);
	const [forceRefresh, setForceRefresh] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		getAging({ segments: key ? (key.split(',') as Segmento[]) : [], agencyId, asOf, refresh: forceRefresh })
			.then((res) => {
				if (cancelled) return;
				setData(res.data);
				setCachedAt(res.cachedAt);
			})
			.catch((err) => {
				if (!cancelled) setError(extractReceivablesError(err));
			})
			.finally(() => {
				if (cancelled) return;
				setLoading(false);
				setForceRefresh(false);
			});
		return () => {
			cancelled = true;
		};
	}, [key, agencyId, asOf, tick, forceRefresh]);

	const refetch = useCallback((opts?: { refresh?: boolean }) => {
		if (opts?.refresh) setForceRefresh(true);
		setTick((t) => t + 1);
	}, []);

	return { data, loading, error, cachedAt, refetch };
}
